import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { publicSupabase } from "@/integrations/supabase/publicClient";
import { Skeleton } from "@/components/ui/skeleton";

// Fallback images for projects without uploaded images
import nieuwesteigerDesktop from "@/assets/portfolio/nieuwesteiger-desktop.png";
import gezondeleefstijlcoachDesktop from "@/assets/portfolio/gezondeleefstijlcoach-desktop.png";
import stucwerkDesktop from "@/assets/portfolio/stucwerk-groningen-desktop.png";
import alwijnDesktop from "@/assets/portfolio/alwijn-desktop.png";
import boezenreclameDesktop from "@/assets/portfolio/boezenreclame-desktop.png";

// Map of fallback images by slug
const fallbackImages: Record<string, string> = {
  "nieuwe-steiger": nieuwesteigerDesktop,
  "gezonde-leefstijlcoach": gezondeleefstijlcoachDesktop,
  "stucwerk-groningen": stucwerkDesktop,
  "alwijn-slijterij": alwijnDesktop,
  "boezen-reclame": boezenreclameDesktop,
};

export function PortfolioPreview() {
  const { data: projects, isLoading } = useQuery({
    queryKey: ['portfolio-projects-featured'],
    queryFn: async () => {
      const { data, error } = await publicSupabase
        .from('portfolio_projects')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  // Get featured project (marked as featured) or first one
  const featuredProject = projects?.find(p => p.is_featured) || projects?.[0];
  
  // Get other projects (excluding featured), limit to 3
  const previewItems = projects?.filter(p => p.id !== featuredProject?.id).slice(0, 3) || [];

  const getImage = (project: typeof featuredProject) => {
    if (!project) return null;
    return project.featured_image || fallbackImages[project.slug] || null;
  };

  if (isLoading) {
    return (
      <section className="section-padding bg-muted">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center mb-16">
            <div>
              <Skeleton className="h-4 w-20 mb-4" />
              <Skeleton className="h-10 w-48 mb-4" />
              <Skeleton className="h-20 w-full mb-8" />
              <Skeleton className="h-12 w-40" />
            </div>
            <Skeleton className="aspect-video hidden lg:block" />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-[4/3] rounded-xl" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-5 w-32" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!projects || projects.length === 0) {
    return null;
  }

  const featuredImage = getImage(featuredProject);

  return (
    <section className="section-padding bg-muted">
      <div className="container-wide">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center mb-16">
          <div>
            <p className="text-sm uppercase tracking-widest mb-4 text-primary">Projecten</p>
            <h2>Eerder werk</h2>
            <p className="mt-4 text-muted-foreground">
              Van websites en webshops tot SEO-trajecten. Een greep uit projecten 
              waar ik aan heb gewerkt.
            </p>
            <div className="mt-8">
              <Button asChild size="lg">
                <Link to="/projecten">
                  Bekijk alle projecten
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
          
          {/* Featured project - in monitor mockup */}
          {featuredProject && featuredImage && (
            <Link to={`/projecten/${featuredProject.slug}`} className="group hidden lg:block">
              <div className="relative">
                {/* Monitor frame */}
                <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-t-xl p-2 pb-0">
                  <div className="flex items-center justify-center gap-2 pb-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-600"></div>
                  </div>
                  <div className="aspect-video bg-white rounded-t-sm overflow-hidden">
                    <img 
                      src={featuredImage} 
                      alt={featuredProject.title}
                      className="w-full h-full object-cover object-top transition-transform group-hover:scale-105"
                    />
                  </div>
                </div>
                <div className="bg-gradient-to-b from-gray-800 to-gray-700 h-4 rounded-b-lg flex items-center justify-center">
                  <div className="w-8 h-0.5 bg-gray-600 rounded-full"></div>
                </div>
                <div className="flex justify-center">
                  <div className="w-12 h-6 bg-gradient-to-b from-gray-700 to-gray-600"></div>
                </div>
                <div className="flex justify-center">
                  <div className="w-24 h-1.5 bg-gradient-to-b from-gray-600 to-gray-500 rounded-full"></div>
                </div>
              </div>
            </Link>
          )}
        </div>

        {/* Portfolio preview grid - remaining items */}
        {previewItems.length > 0 && (
          <div className="grid md:grid-cols-3 gap-6">
            {previewItems.map((item) => {
              const image = getImage(item);
              return (
                <Link
                  key={item.id}
                  to={`/projecten/${item.slug}`}
                  className="group block"
                >
                  <div className="aspect-[4/3] bg-gradient-to-br from-muted to-muted/50 rounded-xl overflow-hidden border border-border hover:border-primary/40 hover:shadow-lg transition-all">
                    {image ? (
                      <img
                        src={image}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-muted-foreground text-sm">[Afbeelding]</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-3">
                    <span className="text-xs text-primary font-medium">{item.category}</span>
                    <h3 className="text-sm font-medium group-hover:text-primary transition-colors">{item.title}</h3>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
